/**
 * AcceptInvitationPage Component
 *
 * Page for users to accept setter invitations via the token in the URL.
 * Handles the full flow: login if needed, show gym info, accept invitation.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { getFirebaseFunctions } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';

/**
 * Page states
 */
type PageState = 'loading' | 'needs-login' | 'ready-to-accept' | 'accepting' | 'success' | 'error';

/**
 * Error types for better error messages
 */
interface InvitationError {
  type: 'not-found' | 'expired' | 'already-accepted' | 'unknown';
  message: string;
}

/**
 * AcceptInvitationPage - handles invitation acceptance flow
 */
export const AcceptInvitationPage = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading, signIn } = useAuth();

  const [pageState, setPageState] = useState<PageState>('loading');
  const [error, setError] = useState<InvitationError | null>(null);
  const [gymName, setGymName] = useState<string>('');
  const [gymId, setGymId] = useState<string>('');

  // Determine initial page state based on auth
  useEffect(() => {
    if (authLoading) {
      setPageState('loading');
      return;
    }

    if (!user) {
      setPageState('needs-login');
    } else {
      setPageState('ready-to-accept');
    }
  }, [user, authLoading]);

  /**
   * Handle accepting the invitation
   */
  const handleAccept = async () => {
    if (!token || !user) return;

    setPageState('accepting');
    setError(null);

    try {
      const functions = getFirebaseFunctions();
      const acceptInvitation = httpsCallable<
        { token: string },
        { success: boolean; gymId: string; gymName: string }
      >(functions, 'acceptInvitation');

      const result = await acceptInvitation({ token });

      setGymName(result.data.gymName);
      setGymId(result.data.gymId);
      setPageState('success');
    } catch (err) {
      const error = err as { code?: string; message?: string };

      let invitationError: InvitationError;

      if (error.code === 'functions/not-found') {
        invitationError = {
          type: 'not-found',
          message: 'This invitation link is invalid or has already been used.',
        };
      } else if (
        error.code === 'functions/failed-precondition' &&
        error.message?.includes('expired')
      ) {
        invitationError = {
          type: 'expired',
          message: 'This invitation has expired. Please ask the gym owner to send a new one.',
        };
      } else if (
        error.code === 'functions/failed-precondition' &&
        error.message?.includes('accepted')
      ) {
        invitationError = {
          type: 'already-accepted',
          message: 'This invitation has already been accepted.',
        };
      } else {
        invitationError = {
          type: 'unknown',
          message: error.message ?? 'Failed to accept invitation. Please try again.',
        };
      }

      setError(invitationError);
      setPageState('error');
    }
  };

  /**
   * Handle sign in and return to accept
   */
  const handleSignIn = async () => {
    try {
      await signIn();
      // Auth state change will update pageState
    } catch {
      setError({
        type: 'unknown',
        message: 'Failed to sign in. Please try again.',
      });
      setPageState('error');
    }
  };

  /**
   * Navigate to the gym page
   */
  const handleGoToGym = () => {
    navigate(`/gyms/${gymId}`);
  };

  // Validate token exists
  if (!token) {
    return (
      <PageLayout>
        <ErrorDisplay
          title="Invalid Invitation"
          message="This invitation link is missing the required token."
          showHomeLink
        />
      </PageLayout>
    );
  }

  // Render based on page state
  switch (pageState) {
    case 'loading':
      return (
        <PageLayout>
          <LoadingDisplay message="Loading invitation..." />
        </PageLayout>
      );

    case 'needs-login':
      return (
        <PageLayout>
          <div style={{ textAlign: 'center' }}>
            <h1
              style={{
                fontSize: '1.5rem',
                fontWeight: 700,
                color: '#1f2937',
                marginBottom: '16px',
              }}
            >
              Accept Setter Invitation
            </h1>
            <p
              style={{
                color: '#6b7280',
                marginBottom: '24px',
              }}
            >
              Please sign in to accept your invitation and become a route setter.
            </p>
            <button
              onClick={handleSignIn}
              style={{
                padding: '14px 32px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              Sign in with Google
            </button>
          </div>
        </PageLayout>
      );

    case 'ready-to-accept':
      return (
        <PageLayout>
          <div style={{ textAlign: 'center' }}>
            <h1
              style={{
                fontSize: '1.5rem',
                fontWeight: 700,
                color: '#1f2937',
                marginBottom: '16px',
              }}
            >
              Accept Setter Invitation
            </h1>
            <p
              style={{
                color: '#6b7280',
                marginBottom: '8px',
              }}
            >
              You&apos;ve been invited to become a route setter.
            </p>
            <p
              style={{
                color: '#374151',
                marginBottom: '24px',
              }}
            >
              Signed in as <strong>{user?.displayName ?? user?.email}</strong>
            </p>
            <p
              style={{
                fontSize: '14px',
                color: '#6b7280',
                marginBottom: '24px',
              }}
            >
              As a setter, you&apos;ll be able to create and manage routes at the gym.
            </p>
            <button
              onClick={handleAccept}
              style={{
                padding: '14px 32px',
                backgroundColor: '#22c55e',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Accept Invitation
            </button>
          </div>
        </PageLayout>
      );

    case 'accepting':
      return (
        <PageLayout>
          <LoadingDisplay message="Accepting invitation..." />
        </PageLayout>
      );

    case 'success':
      return (
        <PageLayout>
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '64px',
                height: '64px',
                backgroundColor: '#dcfce7',
                borderRadius: '50%',
                marginBottom: '24px',
              }}
            >
              <span
                style={{
                  fontSize: '32px',
                  color: '#22c55e',
                }}
              >
                {'\u2713'}
              </span>
            </div>
            <h1
              style={{
                fontSize: '1.5rem',
                fontWeight: 700,
                color: '#1f2937',
                marginBottom: '16px',
              }}
            >
              You&apos;re now a setter!
            </h1>
            <p
              style={{
                color: '#6b7280',
                marginBottom: '24px',
              }}
            >
              You can now create and manage routes at <strong>{gymName}</strong>.
            </p>
            <div
              style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              <button
                onClick={handleGoToGym}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Go to {gymName}
              </button>
              <Link
                to="/setter"
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#e5e7eb',
                  color: '#374151',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 500,
                  textDecoration: 'none',
                }}
              >
                Setter Dashboard
              </Link>
            </div>
          </div>
        </PageLayout>
      );

    case 'error':
      return (
        <PageLayout>
          <ErrorDisplay
            title={getErrorTitle(error?.type)}
            message={error?.message ?? 'An error occurred'}
            showHomeLink
            showRetry={error?.type === 'unknown'}
            onRetry={() => setPageState('ready-to-accept')}
          />
        </PageLayout>
      );

    default:
      return null;
  }
};

/**
 * Get error title based on error type
 */
const getErrorTitle = (type?: string): string => {
  switch (type) {
    case 'not-found':
      return 'Invalid Invitation';
    case 'expired':
      return 'Invitation Expired';
    case 'already-accepted':
      return 'Already Accepted';
    default:
      return 'Something went wrong';
  }
};

/**
 * Page layout wrapper
 */
const PageLayout = ({ children }: { children: React.ReactNode }) => (
  <main
    style={{
      minHeight: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 20px',
      backgroundColor: '#f9fafb',
    }}
  >
    <div
      style={{
        maxWidth: '480px',
        width: '100%',
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '40px',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
      }}
    >
      {children}
    </div>
  </main>
);

/**
 * Loading display component
 */
const LoadingDisplay = ({ message }: { message: string }) => (
  <div style={{ textAlign: 'center' }}>
    <div
      style={{
        display: 'inline-block',
        width: '48px',
        height: '48px',
        border: '3px solid #e5e7eb',
        borderTopColor: '#3b82f6',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        marginBottom: '24px',
      }}
    />
    <p style={{ color: '#6b7280' }}>{message}</p>
    <style>{`
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

/**
 * Error display component
 */
const ErrorDisplay = ({
  title,
  message,
  showHomeLink,
  showRetry,
  onRetry,
}: {
  title: string;
  message: string;
  showHomeLink?: boolean;
  showRetry?: boolean;
  onRetry?: () => void;
}) => (
  <div style={{ textAlign: 'center' }}>
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '64px',
        height: '64px',
        backgroundColor: '#fef2f2',
        borderRadius: '50%',
        marginBottom: '24px',
      }}
    >
      <span
        style={{
          fontSize: '32px',
          color: '#ef4444',
        }}
      >
        !
      </span>
    </div>
    <h1
      style={{
        fontSize: '1.5rem',
        fontWeight: 700,
        color: '#1f2937',
        marginBottom: '16px',
      }}
    >
      {title}
    </h1>
    <p
      style={{
        color: '#6b7280',
        marginBottom: '24px',
      }}
    >
      {message}
    </p>
    <div
      style={{
        display: 'flex',
        gap: '12px',
        justifyContent: 'center',
        flexWrap: 'wrap',
      }}
    >
      {showRetry && onRetry && (
        <button
          onClick={onRetry}
          style={{
            padding: '12px 24px',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          Try Again
        </button>
      )}
      {showHomeLink && (
        <Link
          to="/"
          style={{
            padding: '12px 24px',
            backgroundColor: '#e5e7eb',
            color: '#374151',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 500,
            textDecoration: 'none',
          }}
        >
          Go Home
        </Link>
      )}
    </div>
  </div>
);
